// The lit stretches of a shipping lane (mockup round 139 card 20 with the
// double rule of card 05, R3072; ui-map-interface.md §17.3.16,
// v2-the-deep.md §7.1 "Lit water", §7.2, §11.3).
//
// Chart card 16 draws a lane as a road with two edges, and the road lights
// where a fleet of the faction takes a lift: inside the reach of a lane
// light of the faction that acts, and on lit water. This module measures
// the tracks of a lane (`lanechain.ts`) and cuts each into runs along it:
// unlit, lit by a light, and lit water. A light lights `reach` both ways
// along the track from the point of each leg nearest it, so two lights
// whose reaches meet make one stretch, and a stretch follows a bend. Lit
// water is the mirror of `World.MaskFairways` of the server: the chain
// cells from the first buoy to the last buoy of each chain.
//
// Everything here is pure data, so a test reads it with no engine, and the
// map reads it only on a flip, never once a frame (§10). The relay point of
// card 20 is the one figure a frame asks for (`relayAt`).

import type { Fairway, FairwayBuoy } from './gen/proto/aquaphobia';
import type { ChainPt, ChainTrack } from './lanechain';
import { cellAt, cellCenterX, cellCenterY, mpxToPx } from './world';

/** A point on the map, world px. */
export type XY = readonly [number, number];

/** A line measured: its points (a closed line repeats its first point at
 *  the end), the distance along it at each point, and its length. */
export interface LaneLine { pts: readonly XY[]; at: readonly number[]; len: number; closed: boolean }

/** A stretch of a line, from the distance `a` to the distance `b`. */
export type Interval = [number, number];

/** What lights a run: a lane light, lit water, or nothing. */
export type RunKind = 'light' | 'water' | null;

/** One run of a line: its stretch and what lights it. */
export interface LaneRun { a: number; b: number; kind: RunKind }

/** Measures a line of points. */
export function measureLine(points: readonly XY[], closed = false): LaneLine {
  const pts: XY[] = points.map((p) => [p[0], p[1]] as const);
  if (closed && pts.length > 1) pts.push(pts[0]);
  const at = [0];
  for (let i = 1; i < pts.length; i++) {
    at.push(at[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  }
  return { pts, at, len: at[at.length - 1] ?? 0, closed };
}

/** Measures one lane track of the chain walk. */
export function measureTrack(t: ChainTrack): LaneLine {
  return measureLine(t.pts, t.closed);
}

/** The point at the distance `s` along a line, with the unit tangent. */
export function pointAt(l: LaneLine, s: number): { x: number; y: number; ux: number; uy: number } {
  const n = l.pts.length;
  if (n === 0) return { x: 0, y: 0, ux: 1, uy: 0 };
  if (n === 1) return { x: l.pts[0][0], y: l.pts[0][1], ux: 1, uy: 0 };
  let i = 1;
  while (i < n - 1 && (l.at[i] < s || l.at[i] === l.at[i - 1])) i++;
  const a = l.pts[i - 1], b = l.pts[i], len = l.at[i] - l.at[i - 1];
  const u = len > 0 ? Math.max(0, Math.min(1, (s - l.at[i - 1]) / len)) : 0;
  const dx = b[0] - a[0], dy = b[1] - a[1];
  return { x: a[0] + dx * u, y: a[1] + dy * u, ux: len > 0 ? dx / len : 1, uy: len > 0 ? dy / len : 0 };
}

/** The part of a line from `a` to `b`, as points. */
export function subLine(l: LaneLine, a: number, b: number): XY[] {
  const p = pointAt(l, a), q = pointAt(l, b);
  const out: XY[] = [[p.x, p.y]];
  for (let i = 0; i < l.pts.length; i++) if (l.at[i] > a && l.at[i] < b) out.push(l.pts[i]);
  out.push([q.x, q.y]);
  return out;
}

/** Joins the stretches that touch or overlap, in the order along. */
export function mergeIntervals(iv: readonly Interval[]): Interval[] {
  const out: Interval[] = [];
  for (const v of [...iv].sort((p, q) => p[0] - q[0])) {
    const o = out[out.length - 1];
    if (o && v[0] <= o[1]) o[1] = Math.max(o[1], v[1]);
    else out.push([v[0], v[1]]);
  }
  return out;
}

/** Adds a stretch: a closed line wraps it, an open line cuts it at its ends. */
function addInterval(out: Interval[], l: LaneLine, a: number, b: number): void {
  if (!(l.len > 0)) return;
  if (l.closed) {
    if (b - a >= l.len) { out.push([0, l.len]); return; }
    if (a < 0) { out.push([a + l.len, l.len]); a = 0; }
    if (b > l.len) { out.push([0, b - l.len]); b = l.len; }
    out.push([a, b]);
    return;
  }
  a = Math.max(0, a); b = Math.min(l.len, b);
  if (a < b) out.push([a, b]);
}

/** The distance along a line of the point of leg i nearest p, and the
 *  distance of p from it. */
function nearOnLeg(l: LaneLine, i: number, px: number, py: number): [number, number] {
  const a = l.pts[i], b = l.pts[i + 1];
  const dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy;
  const u = l2 > 0 ? Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / l2)) : 0;
  return [l.at[i] + u * (l.at[i + 1] - l.at[i]), Math.hypot(a[0] + dx * u - px, a[1] + dy * u - py)];
}

/** The point of a line nearest p: its distance along, and the distance of
 *  p from the line. */
export function nearest(l: LaneLine, px: number, py: number): [number, number] {
  let best: [number, number] = [0, Infinity];
  for (let i = 0; i + 1 < l.pts.length; i++) {
    const h = nearOnLeg(l, i, px, py);
    if (h[1] < best[1]) best = h;
  }
  return best;
}

/**
 * The stretches the lights light on a line, merged: `reach` both ways along
 * the line from the point of each leg that passes within `reach` of a light.
 */
export function lightIntervals(l: LaneLine, lights: readonly ChainPt[], reach: number): Interval[] {
  if (!(reach > 0)) return [];
  const out: Interval[] = [];
  for (const o of lights) {
    for (let i = 0; i + 1 < l.pts.length; i++) {
      const [s, d] = nearOnLeg(l, i, o.x, o.y);
      if (d <= reach) addInterval(out, l, s - reach, s + reach);
    }
  }
  return mergeIntervals(out);
}

/** The distance along a line of each light within `reach` of it, in order. */
export function lightStops(l: LaneLine, lights: readonly ChainPt[], reach: number): { s: number; light: ChainPt }[] {
  const out: { s: number; light: ChainPt }[] = [];
  for (const o of lights) {
    const [s, d] = nearest(l, o.x, o.y);
    if (d <= reach) out.push({ s, light: o });
  }
  return out.sort((p, q) => p.s - q.s);
}

/**
 * The lit water cells (v2-the-deep.md §7.1, R2976): the chain cells from
 * the first buoy to the last buoy of each chain, along the chain. It
 * mirrors `World.MaskFairways`: the chains of every fairway in order, each
 * buoy projected to the nearest chain cell (the lowest index on a tie),
 * and a chain with fewer than two buoy positions has none.
 */
export function litWaterCells(fairways: readonly Fairway[], buoys: readonly FairwayBuoy[]): Set<number> {
  const chains = fairways.flatMap((f) => f.chains.map((c) => c.cells));
  const first = chains.map((c) => c.length), last = chains.map(() => -1);
  for (const b of buoys) {
    const ch = chains[b.chain];
    if (!ch) continue;
    const x = mpxToPx(b.xMpx), y = mpxToPx(b.yMpx);
    let at = -1, best = 0;
    ch.forEach((c, i) => {
      const dx = cellCenterX(c) - x, dy = cellCenterY(c) - y, d = dx * dx + dy * dy;
      if (at < 0 || d < best) { at = i; best = d; }
    });
    first[b.chain] = Math.min(first[b.chain], at);
    last[b.chain] = Math.max(last[b.chain], at);
  }
  const out = new Set<number>();
  chains.forEach((ch, i) => { for (let k = first[i]; k <= last[i] && first[i] < last[i]; k++) out.add(ch[k]); });
  return out;
}

/** The stretches of a line on lit water, read every `step` px along it. */
export function waterIntervals(l: LaneLine, cells: ReadonlySet<number>, step: number): Interval[] {
  if (cells.size === 0 || !(l.len > 0) || !(step > 0)) return [];
  const out: Interval[] = [];
  let open = -1;
  for (let s = 0; ; s = Math.min(l.len, s + step)) {
    const p = pointAt(l, s);
    const lit = cells.has(cellAt(p.x, p.y));
    if (lit && open < 0) open = s;
    else if (!lit && open >= 0) { out.push([open, s]); open = -1; }
    if (s >= l.len) break;
  }
  if (open >= 0 && open < l.len) out.push([open, l.len]);
  return out;
}

/** Cuts a line of length `len` into runs; a light wins over lit water. */
export function runsOf(len: number, light: readonly Interval[], water: readonly Interval[]): LaneRun[] {
  if (!(len > 0)) return [];
  const cuts = [...new Set([0, len, ...light.flat(), ...water.flat()])].filter((s) => s >= 0 && s <= len).sort((p, q) => p - q);
  const inside = (iv: readonly Interval[], s: number) => iv.some(([a, b]) => s >= a && s <= b);
  const out: LaneRun[] = [];
  for (let i = 1; i < cuts.length; i++) {
    const a = cuts[i - 1], b = cuts[i], m = (a + b) / 2;
    const kind: RunKind = inside(light, m) ? 'light' : inside(water, m) ? 'water' : null;
    const o = out[out.length - 1];
    if (o && o.kind === kind) o.b = b;
    else out.push({ a, b, kind });
  }
  return out;
}

/**
 * The relay point of card 20: one point runs along a lit run of length
 * `len`, light to light, at `speed` px a second, and waits `gap` px of
 * travel before it runs again. `phase` sets it apart from its neighbours.
 * It answers the distance along the run, or null while the point waits.
 */
export function relayAt(len: number, phase: number, t: number, speed: number, gap: number): number | null {
  if (!(len > 0) || !(speed > 0)) return null;
  const cyc = len + Math.max(0, gap);
  const d = (((t * speed + phase) % cyc) + cyc) % cyc;
  return d <= len ? d : null;
}

/** A hostile lane light, as the picture holds it. */
export interface FoeLight extends ChainPt { faction: number }

/**
 * The chain segments an enemy sees (v2-the-deep.md §7.2 rule 3, §11.3 rule
 * 1): each two lane lights of one hostile faction at most `within` px
 * apart. The enemy learns that a lit road runs there, not its track.
 */
export function foeChains(lights: readonly FoeLight[], within: number): [FoeLight, FoeLight][] {
  const out: [FoeLight, FoeLight][] = [];
  for (let i = 0; i < lights.length; i++) {
    for (let j = i + 1; j < lights.length; j++) {
      const a = lights[i], b = lights[j];
      if (a.faction === b.faction && Math.hypot(a.x - b.x, a.y - b.y) <= within) out.push([a, b]);
    }
  }
  return out;
}

/**
 * A line moved `d` px to its left (the normal `(-uy, ux)`), with a mitre at
 * each corner, held to `2 |d|` so a sharp turn does not spike.
 */
export function offsetLine(pts: readonly XY[], d: number): XY[] {
  const n = pts.length;
  if (n < 2 || d === 0) return pts.slice();
  const norms: XY[] = [];
  for (let i = 1; i < n; i++) {
    const dx = pts[i][0] - pts[i - 1][0], dy = pts[i][1] - pts[i - 1][1], L = Math.hypot(dx, dy);
    norms.push(L > 0 ? [-dy / L, dx / L] : (norms[norms.length - 1] ?? [0, 0]));
  }
  const out: XY[] = [];
  for (let i = 0; i < n; i++) {
    const a = norms[Math.max(0, i - 1)], b = norms[Math.min(norms.length - 1, i)];
    let mx = a[0] + b[0], my = a[1] + b[1];
    const ml = Math.hypot(mx, my);
    if (ml < 1e-9) { mx = b[0]; my = b[1]; } else { mx /= ml; my /= ml; }
    const cos = mx * b[0] + my * b[1];
    const k = Math.min(2 * Math.abs(d), Math.abs(d / (cos > 1e-6 ? cos : 1e-6))) * Math.sign(d);
    out.push([pts[i][0] + mx * k, pts[i][1] + my * k]);
  }
  return out;
}
